import { FC, ReactElement, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Collapse, Stack, Typography, useMediaQuery } from "@mui/material";
import { Theme as MuiTheme, alpha } from "@mui/material/styles";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { vwScale, responsiveFontSizes } from "@/utils/responsive";
import { tokens } from "@/tokens";

export const NavItem: FC<ISidebarItem> = ({
  href,
  icon,
  icon2,
  title,
  isSidebarExtended,
  children,
}): ReactElement => {
  const router = useLocation();
  const navigate = useNavigate();
  const mdUp = useMediaQuery((theme: MuiTheme) => theme.breakpoints.up("md"), {
    defaultMatches: true,
    noSsr: false,
  });

  const extendedSidebar = useCallback(() => {
    if (mdUp) {
      return isSidebarExtended;
    }
    return true;
  }, [mdUp, isSidebarExtended]);

  const active = useMemo(() => {
    if (children?.length) {
      return children.some((item) => {
        const activetab =
          router.pathname.split("/").length > 2
            ? item.href.split("/")[2] === router.pathname.split("/")[2]
            : item.href === `/${router.pathname.split("/")[1]}`;
        return activetab;
      });
    }
    if (router.pathname === href) return true;
    const hrefSegments = href.split('/').filter(Boolean);
    const pathSegments = router.pathname.split('/').filter(Boolean);
    if (hrefSegments.length === 0 || pathSegments.length === 0) return false;
    // Dashboard has multiple sibling items (ACE Analytics, Potential Savings)
    // under the same root segment, so match on the first TWO segments to keep
    // them distinct. Guard on pathSegments[0] so a future `/other/analytics`
    // route can't falsely highlight `/dashboard/analytics`.
    if (hrefSegments[0] === 'dashboard') {
      return (
        pathSegments[0] === 'dashboard' &&
        hrefSegments.length >= 2 &&
        pathSegments.length >= 2 &&
        hrefSegments[1] === pathSegments[1]
      );
    }
    // All other sidebar items own their root segment — highlight on any sub-route.
    return hrefSegments[0] === pathSegments[0];
  }, [router.pathname, href, children]);

  const labelSx = (isExtended: boolean) => ({
    fontWeight: tokens.fontWeight.semibold,
    lineHeight: isExtended ? vwScale(18, 14) : vwScale(14, 11),
    fontSize: isExtended ? responsiveFontSizes.sm : tokens.fontSize["2xs"],
    letterSpacing: "-0.3px",
    textAlign: "center",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: isExtended ? "calc(100% - 24px)" : "100%",
  });

  if (children && children.length > 0) {
    const isChildActive = active;
    return (
      <>
        <Box sx={{ position: "relative" }}>
          {active && (
            <Box
              sx={{
                position: "absolute",
                top: "6px",
                bottom: 0,
                left: "-16px",
                width: 3,
                height: "18px",
                borderRadius: "0px 3px 3px 0px",
                backgroundColor: "#335CFF",
                display: 'none'
              }}
            />
          )}
          <Stack
            direction={extendedSidebar() ? "row" : "column"}
            alignItems={"center"}
            justifyContent={extendedSidebar() ? "flex-start" : "center"}
            sx={{
              background:
                active || isChildActive
                  ? "rgba(11, 73, 75, 0.2)"
                  : "",
              borderRadius: tokens.radius.md,
              width: extendedSidebar() ? "94%" : "36px",
              height: extendedSidebar() ? "32px" : "36px",
              mx: "auto",
              cursor: "pointer",
              pl: extendedSidebar() ? 1 : 0,
              p: extendedSidebar() ? undefined : "8px",
              my: extendedSidebar() ? 0.25 : 0.1,
            }}
            spacing={extendedSidebar() ? 1 : 0}
            onClick={() => {
              window.dispatchEvent(new Event('td:expandSidebar'));
              if (children && children[0]) {
                navigate(children[0].href);
              }
            }}
          >
            {active ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: extendedSidebar() ? 16 : 20, height: extendedSidebar() ? 16 : 20 }}>
                <Box sx={{ width: extendedSidebar() ? 16 : 20, height: extendedSidebar() ? 16 : 20, color: tokens.brand.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: extendedSidebar() ? 16 : 20, height: extendedSidebar() ? 16 : 20 }}>
                <Box sx={{ width: extendedSidebar() ? 16 : 20, height: extendedSidebar() ? 16 : 20, color: tokens.text.secondary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon2}</Box>
              </Box>
            )}

            {extendedSidebar() && (
              <Typography
                color={active ? tokens.brand.primary : tokens.text.secondary}
                sx={labelSx(!!extendedSidebar())}
              >
                {title}
              </Typography>
            )}
          </Stack>
          {extendedSidebar() && (
            <Box
              sx={{
                position: "absolute",
                top: 8,
                right: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {isChildActive ? (
                <IoIosArrowUp fontSize={"10px"} color={tokens.text.muted} />
              ) : (
                <IoIosArrowDown fontSize={"10px"} color={tokens.text.muted} />
              )}
            </Box>
          )}
        </Box>
        {extendedSidebar() && (
          <Collapse
            in={isChildActive}
            timeout="auto"
            unmountOnExit
            sx={{
              pl: 3.5,
              overflow: "hidden",
            }}
          >
            {children.map((item) => {
              const activetab =
                router.pathname.split("/").length > 2
                  ? item.href.split("/")[2] === router.pathname.split("/")[2]
                  : item.href === `/${router.pathname.split("/")[1]}`;
              return (
                <Box sx={{ position: "relative" }} key={item.title}>
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      bottom: 4,
                      left: 10,
                      width: "0.8px",
                      height: extendedSidebar() ? "20px" : "36px",
                      backgroundColor: activetab ? tokens.brand.accent : "#20383A",
                    }}
                  />
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    sx={{
                      background: activetab ? "rgba(255, 255, 255, 0.08)" : "",
                      borderRadius: "4px",
                      width: "90%",
                      height: "24px",
                      mx: "auto",
                      cursor: "pointer",
                      pl: 0.5,
                    }}
                    onClick={() => {
                      window.dispatchEvent(new Event('td:expandSidebar'));
                      navigate(item.href);
                    }}
                  >
                    <Typography
                      sx={{ ...labelSx(true), color: activetab ? tokens.brand.accent : tokens.text.secondary }}
                    >
                      {item.title}
                    </Typography>
                  </Stack>
                </Box>
              );
            })}
          </Collapse>
        )}
      </>
    );
  }

  return (
    <Box sx={{ position: "relative" }}>
      {active && (
        <Box
          sx={{
            position: "absolute",
            top: "6px",
            bottom: 0,
            left: "-16px",
            width: 3,
            height: "18px",
            borderRadius: "0px 3px 3px 0px",
            backgroundColor: "#335CFF",
            display: 'none'
          }}
        />
      )}
      <Stack
        direction={extendedSidebar() ? "row" : "column"}
        alignItems={"center"}
        justifyContent={extendedSidebar() ? "flex-start" : "center"}
        sx={{
          background: active ? "rgba(11, 73, 75, 0.2)" : "transparent",
          borderRadius: tokens.radius.md,
          width: extendedSidebar() ? "94%" : "36px",
          height: extendedSidebar() ? "32px" : "36px",
          mx: "auto",
          cursor: "pointer",
          pl: extendedSidebar() ? 1 : 0,
          p: extendedSidebar() ? undefined : "8px",
          my: extendedSidebar() ? 0.25 : 0.1,
          transition: "background-color 0.2s ease, color 0.2s ease",
          "&:hover": {
            background: active
              ? "rgba(11, 73, 75, 0.25)"
              : alpha(tokens.text.primary, 0.04),
          },
        }}
        spacing={extendedSidebar() ? 1 : 0}
        onClick={() => {
          window.dispatchEvent(new Event('td:expandSidebar'));
          navigate(href);
        }}
      >
        {active ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: extendedSidebar() ? 16 : 20, height: extendedSidebar() ? 16 : 20 }}>
            <Box sx={{ width: extendedSidebar() ? 16 : 20, height: extendedSidebar() ? 16 : 20, color: tokens.brand.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: extendedSidebar() ? 16 : 20, height: extendedSidebar() ? 16 : 20 }}>
            <Box sx={{ width: extendedSidebar() ? 16 : 20, height: extendedSidebar() ? 16 : 20, color: tokens.text.secondary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon2}</Box>
          </Box>
        )}

        {extendedSidebar() && (
          <Typography
            color={active ? tokens.brand.primary : tokens.text.secondary}
            sx={labelSx(!!extendedSidebar())}
          >
            {title}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};
